import React, { useCallback, useEffect, useRef, useState } from "react";
import { firebaseErrorMessage, isRetryableError } from "../utils/firebaseErrors";
import { reportError } from "../utils/reportError";
import "./ui.css";

/**
 * AsyncState – jednotné stavy obrazovek, které čtou Firestore:
 * loading (spinner až po 150 ms, ať UI nebliká), empty (ikona + titulek +
 * volitelná akce), error (česká hláška + „Zkusit znovu", nikdy reload okna)
 * a ready (children). Odstraňuje nekonečné „Načítám data...", bliknutí
 * „nic nenalezeno" během načítání a chyby vydávané za prázdný seznam.
 *
 * Hook useAsync(fn, deps) tyhle stavy obsluhuje: vrací { status, data,
 * error, reload }; prázdné pole z fn znamená status "empty".
 */

export type AsyncStatus = "idle" | "loading" | "error" | "empty" | "ready";

export interface AsyncStateProps {
  status: AsyncStatus;
  /** Text pod spinnerem. */
  loadingText?: string;
  /** Ikona prázdného stavu (bootstrap-icons, např. "bi-calendar-x"). */
  emptyIcon?: string;
  emptyTitle?: string;
  emptyText?: string;
  emptyAction?: { label: string; onClick: () => void };
  error?: unknown;
  onRetry?: () => void;
  /** Kompaktní režim: malý spinner/hláška v řádku místo celoplošného stavu. */
  inline?: boolean;
  /** Vykreslí se jen při status "ready". */
  children?: React.ReactNode;
}

/** Spinner se zpožděním 150 ms – krátká načtení vůbec nebliknou. */
export const LoadingState: React.FC<{ text?: string; inline?: boolean }> = ({
  text = "Načítám…",
  inline = false,
}) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) {
    // Drží místo, aby obsah neposkočil, ale nic neukazuje.
    return inline ? null : <div className="lks-async lks-async--placeholder" />;
  }

  if (inline) {
    return (
      <span className="lks-async-inline" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>{text}</span>
      </span>
    );
  }
  return (
    <div className="lks-async" role="status">
      <div
        className="spinner-border"
        style={{ color: "var(--lks-brand)" }}
        aria-hidden="true"
      />
      <div className="lks-async__text">{text}</div>
    </div>
  );
};

export const EmptyState: React.FC<{
  icon?: string;
  title?: string;
  text?: string;
  action?: { label: string; onClick: () => void };
}> = ({ icon = "bi-inbox", title = "Nic tu není", text, action }) => (
  <div className="lks-async">
    <i className={`bi ${icon} lks-async__icon`} aria-hidden="true" />
    <div className="lks-async__title">{title}</div>
    {text ? <div className="lks-async__text">{text}</div> : null}
    {action ? (
      <button type="button" className="btn btn-primary mt-3" onClick={action.onClick}>
        {action.label}
      </button>
    ) : null}
  </div>
);

export const ErrorState: React.FC<{
  error?: unknown;
  onRetry?: () => void;
  inline?: boolean;
}> = ({ error, onRetry, inline = false }) => {
  const offline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  const message = firebaseErrorMessage(error);
  const icon = offline ? "bi-wifi-off" : "bi-exclamation-triangle";

  if (inline) {
    return (
      <span className="lks-async-inline lks-async-inline--error" role="alert">
        <i className={`bi ${icon}`} aria-hidden="true" />
        <span>{message}</span>
        {onRetry && isRetryableError(error) ? (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
            Zkusit znovu
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <div className="lks-async" role="alert">
      <i className={`bi ${icon} lks-async__icon lks-async__icon--error`} aria-hidden="true" />
      <div className="lks-async__title">
        {offline ? "Jste offline" : "Načtení se nepovedlo"}
      </div>
      <div className="lks-async__text">{message}</div>
      {onRetry ? (
        <button type="button" className="btn btn-primary mt-3" onClick={onRetry}>
          <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />
          Zkusit znovu
        </button>
      ) : null}
    </div>
  );
};

export const AsyncState: React.FC<AsyncStateProps> = ({
  status,
  loadingText,
  emptyIcon,
  emptyTitle,
  emptyText,
  emptyAction,
  error,
  onRetry,
  inline = false,
  children,
}) => {
  if (status === "idle" || status === "loading") {
    return <LoadingState text={loadingText} inline={inline} />;
  }
  if (status === "error") {
    return <ErrorState error={error} onRetry={onRetry} inline={inline} />;
  }
  if (status === "empty") {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        text={emptyText}
        action={emptyAction}
      />
    );
  }
  return <>{children}</>;
};

export interface UseAsyncResult<T> {
  status: AsyncStatus;
  data: T | null;
  error: unknown;
  reload: () => void;
}

/**
 * Načtení dat se stavy pro AsyncState. fn se volá při mountu a změně deps;
 * prázdné pole → "empty". Výsledky překonaných volání (rychlé přepnutí
 * filtru, reload) se zahazují, setState po odmountování se nestane.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList
): UseAsyncResult<T> {
  const [state, setState] = useState<{
    status: AsyncStatus;
    data: T | null;
    error: unknown;
  }>({ status: "loading", data: null, error: null });
  const runIdRef = useRef(0);
  const [reloadTick, setReloadTick] = useState(0);

  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const runId = ++runIdRef.current;
    setState((s) => ({ ...s, status: "loading", error: null }));
    fnRef
      .current()
      .then((data) => {
        if (runId !== runIdRef.current) return;
        const empty = Array.isArray(data) && data.length === 0;
        setState({ status: empty ? "empty" : "ready", data, error: null });
      })
      .catch((err) => {
        if (runId !== runIdRef.current) return;
        reportError("useAsync", err);
        setState({ status: "error", data: null, error: err });
      });
    return () => {
      // Zneplatní běžící volání (odmountování nebo změna deps). runIdRef je
      // počítadlo, ne odkaz na prvek – hodnota v době úklidu je záměrná.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      runIdRef.current++;
    };
    // Závislosti určuje volající (deps); fn se čte přes fnRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadTick]);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  return { status: state.status, data: state.data, error: state.error, reload };
}

export default AsyncState;
